import { useEffect, useRef, useState } from "react";
import type { ChangeState } from "../api/types";

/* The bee bee: a pixel mascot that shows how a change is going.
   The sprite and poses follow the bee guideline: 26 x 21 cells, two wing frames, and a face that
   changes with the state. The words next to it stay the source of truth, so it is decorative. */

export type BeeState = "idle" | "working" | "landed" | "conflict" | "failed" | "review";

/** The pose for a change's state, using the same meaning as StateLabel (purple landed, yellow needs a person, red failed). */
export function beeStateFor(s: ChangeState): BeeState {
  switch (s) {
    case "landed":
    case "verified":
      return "landed";
    case "conflict":
      return "conflict";
    case "review":
      return "review";
    case "rejected":
    case "reverted":
      return "failed";
    case "cancelled":
      return "idle";
    default:
      return "working";
  }
}

const BODY = [
  ".......OO........OO.......",
  ".........OO....OO.........",
  "..........OO..OO..........",
  ".........OOOOOOOO.........",
  ".......OOYYYYYYYDOO.......",
  "......OYhhhYYYYYYYDO......",
  ".....OYhhYYYYYYYYYYDO.....",
  ".....OYhYYYYYYYYYYYDO.....",
  "....OYYYYgeYYYYgeYYYDO....",
  "....OYYYYeeYYYYeeYYYDO....",
  "....OYYYYeeYYYYeeYYYDO....",
  "....OYYCCYYMYYMYYCCYDO....",
  "....OYYYYYYYMMYYYYYYDO....",
  "....OYYYYYYYYYYYYYYYDO....",
  "....OKKKKKKKKKKKKKKKKO....",
  ".....OKKKKKKKKKKKKKKO.....",
  ".....ODYYYYYYYYYYYYDO.....",
  "......OKKKKKKKKKKKKO......",
  "......OOOKKKKKKKKOO.......",
  ".....OO..OOOOOOOO.........",
  "....OO....................",
];

/** Terminal-sized sprite, also used as the agent avatar mark. */
const MINI = [
  ".....O......O.....",
  "......O....O......",
  ".www..OOOOOO..www.",
  "wWWWOOYYYYYDOOWWWw",
  "wwwOYYYYYYYYYDOwww",
  "...OYYeYYYYeYDO...",
  "...OYYeYYYYeYDO...",
  "...OYCYMYYMYCDO...",
  "...ODYYYMMYYYDO...",
  "....OKKKKKKKKO....",
  ".....OODDDDOO.....",
  ".......OOOO.......",
];

const COLS = BODY[0].length;
const ROWS = BODY.length;

type Span = Record<number, [number, number]>;
/** Left wing, row to [first col, last col]. The right wing mirrors it. */
const WINGS: Record<"up" | "down", Span> = {
  up: { 3: [3, 6], 4: [2, 6], 5: [1, 5], 6: [0, 4], 7: [0, 4], 8: [0, 3], 9: [0, 3], 10: [1, 3], 11: [2, 3] },
  down: { 7: [2, 4], 8: [1, 3], 9: [0, 3], 10: [0, 3], 11: [0, 3], 12: [1, 3], 13: [1, 3], 14: [2, 3] },
};

type Cell = [number, number, string];
/** Left eye. The right eye is the same shape six columns over. */
const EYES: Record<"open" | "closed" | "wide", Cell[]> = {
  open: [[8, 9, "g"], [8, 10, "e"], [9, 9, "e"], [9, 10, "e"], [10, 9, "e"], [10, 10, "e"]],
  closed: [[9, 9, "e"], [9, 10, "e"]],
  wide: [[8, 9, "g"], [8, 10, "e"], [9, 9, "e"], [9, 10, "e"], [10, 9, "e"], [10, 10, "e"], [11, 9, "e"], [11, 10, "e"]],
};
const MOUTHS: Record<"smile" | "grin" | "flat" | "frown", Array<[number, number]>> = {
  smile: [[11, 11], [11, 14], [12, 12], [12, 13]],
  grin: [[11, 11], [11, 12], [11, 13], [11, 14], [12, 12], [12, 13]],
  flat: [[12, 11], [12, 12], [12, 13], [12, 14]],
  frown: [[11, 12], [11, 13], [12, 11], [12, 14]],
};

type Pose = { wings: "up" | "down"; eyes: keyof typeof EYES; mouth: keyof typeof MOUTHS; dx: 0 | 1; dy: 0 | 1 };
const REST: Pose = { wings: "up", eyes: "open", mouth: "smile", dx: 0, dy: 0 };

const flap = (t: number, ms: number): "up" | "down" => (Math.floor(t / ms) % 2 ? "down" : "up");

/** The pose at time t (ms since mount). Frame swaps only, never tweened. */
function poseAt(state: BeeState, t: number): Pose {
  switch (state) {
    case "idle":
      return { ...REST, wings: flap(t, 520), eyes: t % 4000 < 120 ? "closed" : "open" };
    case "working":
      return { ...REST, wings: flap(t, 110) };
    case "landed": {
      const l = t % 3600;
      return l < 1200 ? { ...REST, wings: flap(l, 180), mouth: "grin", dy: Math.floor(l / 300) % 2 ? 0 : 1 } : { ...REST, wings: "down", mouth: "grin" };
    }
    case "conflict":
      return { ...REST, eyes: "wide", mouth: "flat" };
    case "failed": {
      const l = t % 3200;
      return { ...REST, wings: "down", eyes: "closed", mouth: "frown", dx: l < 540 ? (Math.floor(l / 90) % 2 as 0 | 1) : 0 };
    }
    case "review":
      return { ...REST, wings: flap(t, 900), mouth: "flat" };
  }
}

const restPose = (state: BeeState): Pose => {
  switch (state) {
    case "landed":
      return { ...REST, wings: "down", mouth: "grin" };
    case "conflict":
      return { ...REST, eyes: "wide", mouth: "flat" };
    case "failed":
      return { ...REST, wings: "down", eyes: "closed", mouth: "frown" };
    case "review":
      return { ...REST, mouth: "flat" };
    default:
      return REST;
  }
};

function compose(p: Pose): string[][] {
  const W = COLS + 1;
  const H = ROWS + 1;
  const g = Array.from({ length: H }, () => Array<string>(W).fill("."));
  const ox = p.dx;
  const oy = 1 - p.dy;
  const base = BODY.map((r) => [...r]);
  for (let r = 8; r <= 12; r++) for (let c = 9; c <= 16; c++) base[r][c] = "Y";
  for (const [r, c, ch] of EYES[p.eyes]) {
    base[r][c] = ch;
    base[r][c + 6] = ch;
  }
  for (const [r, c] of MOUTHS[p.mouth]) base[r][c] = "M";
  base.forEach((row, r) => row.forEach((ch, c) => ch !== "." && (g[r + oy][c + ox] = ch)));
  const placed: Array<[number, number]> = [];
  for (const [r, [l, h]] of Object.entries(WINGS[p.wings])) {
    for (let c = l; c <= h; c++) {
      for (const col of [c, COLS - 1 - c]) {
        const R = Number(r) + oy;
        const C = col + ox;
        if (g[R][C] === ".") {
          g[R][C] = "W";
          placed.push([R, C]);
        }
      }
    }
  }
  for (const [R, C] of placed) {
    const open = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([a, b]) => (g[R + a]?.[C + b] ?? ".") === ".");
    if (open) g[R][C] = "w";
  }
  return g;
}

/** Each sprite character mapped to a color. Honey is the default; the avatar palette is a white bee with dark line art. */
const HONEY: Record<string, string> = {
  O: "var(--honey-line)", K: "var(--honey-line)", e: "var(--honey-line)", M: "var(--honey-line)",
  Y: "var(--honey)", h: "var(--honey-glint)", D: "var(--honey-shade)", g: "#fff", C: "var(--honey-cheek)",
  W: "var(--honey-wing)", w: "var(--honey-wing-edge)",
};
const AVATAR: Record<string, string> = {
  O: "#1f2328", K: "#1f2328", e: "#1f2328", M: "#1f2328", w: "#1f2328",
  Y: "#fff", h: "#fff", D: "#fff", g: "#fff", C: "#fff", W: "#fff",
};

function Sprite({ grid, cell, palette, className }: { grid: string[][]; cell: number; palette: Record<string, string>; className?: string }) {
  const paths: Record<string, string> = {};
  grid.forEach((row, r) => row.forEach((ch, c) => ch !== "." && (paths[ch] = `${paths[ch] ?? ""}M${c} ${r}h1v1h-1z`)));
  const w = grid[0].length;
  return (
    <svg
      width={w * cell}
      height={grid.length * cell}
      viewBox={`0 0 ${w} ${grid.length}`}
      aria-hidden="true"
      className={className}
      style={{ display: "block", shapeRendering: "crispEdges", imageRendering: "pixelated", flex: "none" }}
    >
      {Object.entries(paths).map(([ch, d]) => (
        <path key={ch} d={d} fill={palette[ch]} />
      ))}
    </svg>
  );
}

const reduced = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** The mascot. `cell` is the pixel size of one sprite cell, so use whole numbers. Honors prefers-reduced-motion with one still pose per state. */
export function BeeMascot({ state = "idle", cell = 3, className }: { state?: BeeState; cell?: number; className?: string }) {
  const [pose, setPose] = useState<Pose>(() => restPose(state));
  const last = useRef("");
  useEffect(() => {
    if (reduced()) {
      setPose(restPose(state));
      return;
    }
    const start = performance.now();
    const tick = () => {
      const p = poseAt(state, performance.now() - start);
      const key = JSON.stringify(p);
      if (key !== last.current) {
        last.current = key;
        setPose(p);
      }
    };
    tick();
    const id = setInterval(tick, 60);
    return () => clearInterval(id);
  }, [state]);
  return <Sprite grid={compose(pose)} cell={cell} palette={HONEY} className={className} />;
}

/** The small sprite for agent avatars: 18 x 12 cells, white on the agent's own color. */
export function BeeMascotMini({ cell = 1, className }: { cell?: number; className?: string }) {
  return <Sprite grid={MINI.map((r) => [...r])} cell={cell} palette={AVATAR} className={className} />;
}
